import React from 'react';
import { motion } from 'framer-motion';

/*
 * ClaudeToolIcons — friendly, organic, Claude/Anthropic-flavoured line icons for
 * the Tools sidebar rail. The deliberate counterpoint to the sharp Nike nav set:
 *  - ROUND linecap + linejoin, 2px uniform stroke, hand-bowed (sub-pixel
 *    asymmetric) paths → warm, "almost hand-drawn" character.
 *  - CARTOONISH motion: a shared spring vocabulary (boing / snappy / recoil /
 *    calm) + Disney squash-&-stretch / overshoot / wobble per glyph.
 *  - Monochrome (stroke=currentColor) to fit the rail; the ONE warm pop is the
 *    Claude clay accent (#d97757) on the ACTIVE icon only.
 *
 * Motion is driven by framer variant PROPAGATION from the parent (the tool row,
 * a motion.button): it sets the label rest|hover|tap|active; this icon's
 * motion.span (whole-glyph signature) and any motion sub-parts just follow.
 * prefers-reduced-motion is honoured app-wide via <MotionConfig reducedMotion="user">.
 */

const CLAY = '#d97757'; // Anthropic clay/terracotta — active state only

// Shared spring vocabulary (framer physics).
const BOING = { type: 'spring', stiffness: 500, damping: 15, mass: 1 };   // signature cartoon pop (~2 overshoots)
const SNAPPY = { type: 'spring', stiffness: 400, damping: 25, mass: 0.8 }; // safe default, ~1 overshoot
const RECOIL = { type: 'spring', stiffness: 600, damping: 18, mass: 0.9 }; // squishy tap release
const CALM = { type: 'spring', stiffness: 550, damping: 30, mass: 0.8 };   // quiet active settle
const BACK = [0.34, 1.56, 0.64, 1]; // easeOutBack
const WOBBLE = { duration: 0.5, ease: 'easeInOut' };

// Build a full variant set from a custom hover.
const wrap = (hover) => ({
  rest: { scale: 1, rotate: 0, x: 0, y: 0, transition: CALM },
  hover,
  tap: { scale: 0.92, transition: RECOIL },
  active: { scale: 1, rotate: 0, x: 0, y: 0, transition: CALM },
});

const VB = { transformBox: 'view-box' }; // pivot sub-parts in viewBox coords

// Sub-part variant helpers (for the choreographed icons).
const keyVar = { rest: { scaleY: 1 }, hover: { scaleY: [1, 0.6, 1.15, 1], transition: { duration: 0.3, ease: 'easeInOut' } } };
const staggerG = { rest: {}, hover: { transition: { staggerChildren: 0.025 } } };

const ICONS = {
  'clara-ai': { // spark + satellite — the Clara AI mark, twinkles on hover
    origin: 'center',
    wrap: wrap({ scale: 1.15, rotate: [0, -12, 8, 0], transition: { scale: BOING, rotate: WOBBLE } }),
    svg: '<path d="M11 3.2 Q11.9 8.2 14.5 9.5 L16.6 10.6 Q17 10.9 16.6 11.2 L14.5 12.3 Q11.9 13.6 11 18.6 Q10.1 13.6 7.5 12.3 L5.4 11.2 Q5 10.9 5.4 10.6 L7.5 9.5 Q10.1 8.2 11 3.2 Z" /><path d="M18.4 13.6 Q18.9 16.2 20.2 16.9 L21 17.4 L20.2 17.9 Q18.9 18.6 18.4 21.2 Q17.9 18.6 16.6 17.9 L15.8 17.4 L16.6 16.9 Q17.9 16.2 18.4 13.6 Z" />',
  },
  'vip-calculator': { // crown
    origin: 'bottom',
    wrap: wrap({ scale: 1.13, rotate: [0, -6, 4, 0], transition: { scale: BOING, rotate: WOBBLE } }),
    svg: '<path d="M3.4 8.2 Q3 8 3.2 8.4 L5.3 16.5 Q5.4 17 6 17 L18 17 Q18.6 17 18.7 16.5 L20.8 8.4 Q21 8 20.6 8.2 L16 11.2 L12.3 5.4 Q12 5 11.7 5.4 L8 11.2 Z" /><line x1="5.6" y1="19.4" x2="18.4" y2="19.4" /><circle cx="8" cy="13.8" r="0.6" fill="currentColor" /><circle cx="16" cy="13.8" r="0.6" fill="currentColor" />',
  },
  'hash-explorer': { // magnifier over #
    origin: 'center',
    wrap: wrap({ scale: 1.12, rotate: [0, -8, 5, 0], transition: { scale: SNAPPY, rotate: WOBBLE } }),
    svg: '<circle cx="10" cy="10" r="6.1" /><path d="M14.4 14.6 Q15 15.1 15.9 16 L20 20.1" /><path d="M8.3 7.7 L7.4 12.3" /><path d="M11.7 7.6 L10.8 12.4" /><path d="M6.9 9.1 L13.1 9" /><path d="M6.7 11 L12.9 10.9" />',
  },
  'active-bets': { // die — rolls
    origin: 'center',
    wrap: wrap({
      rotate: [0, -180], y: [0, -4, 0, -1, 0], scale: [1, 1, 1.05, 1],
      transition: { rotate: { duration: 0.55, ease: BACK }, y: { duration: 0.55, ease: 'easeInOut' }, scale: { duration: 0.55 } },
    }),
    svg: '<rect x="4.4" y="4.3" width="15.3" height="15.4" rx="3" /><circle cx="8.4" cy="8.5" r="1.05" fill="currentColor" /><circle cx="15.6" cy="8.4" r="1.05" fill="currentColor" /><circle cx="12" cy="12" r="1.05" fill="currentColor" /><circle cx="8.5" cy="15.6" r="1.05" fill="currentColor" /><circle cx="15.5" cy="15.5" r="1.05" fill="currentColor" />',
  },
  'crash-bet-check': { // rocket — launches
    origin: 'bottom',
    wrap: wrap({
      y: [0, 2, -5, -2, -3], scaleY: [1, 0.88, 1.12, 1], scaleX: [1, 1.08, 0.94, 1], rotate: [0, 0, 6, 0],
      transition: { duration: 0.5, ease: 'easeInOut' },
    }),
    svg: '<path d="M12 2.6 Q15.6 6 15.6 11 Q15.6 13.6 12 16.4 Q8.4 13.6 8.4 11 Q8.4 6 12 2.6 Z" /><circle cx="12" cy="9.4" r="1.7" /><path d="M9.6 14.2 Q7 15 6.6 18.4 Q9 17.4 9.9 16.2" /><path d="M14.4 14.2 Q17 15 17.4 18.4 Q15 17.4 14.1 16.2" /><line x1="12" y1="18.4" x2="12" y2="20" /><line x1="9.7" y1="19.4" x2="9.2" y2="21.2" /><line x1="14.3" y1="19.4" x2="14.8" y2="21.2" />',
  },
  'bonus-checker': { // gift — excited wiggle
    origin: 'bottom',
    wrap: wrap({ rotate: [0, -7, 6, -4, 2, 0], scale: 1.12, transition: { rotate: WOBBLE, scale: BOING } }),
    svg: '<rect x="4.3" y="10.6" width="15.4" height="9.1" rx="2" /><path d="M4.3 13.4 L19.7 13.3" /><path d="M12 10.6 L12 19.7" /><path d="M12 10.4 Q9.4 10.4 8.3 8.9 Q7.4 7.4 9 7 Q11 6.8 12 10.4" /><path d="M12 10.4 Q14.6 10.4 15.7 8.9 Q16.6 7.4 15 7 Q13 6.8 12 10.4" /><path d="M12 6 L12 4.2" /><path d="M9.4 5.4 L8.6 3.9" /><path d="M14.6 5.4 L15.4 3.9" />',
  },
  'balance-calculator': { // calculator — keys ripple
    origin: 'center',
    wrap: wrap({ scale: 1.1, rotate: -3, transition: { scale: SNAPPY, rotate: SNAPPY } }),
    render: () => (
      <>
        <rect x="5.3" y="2.6" width="13.4" height="18.8" rx="2.6" />
        <rect x="7.7" y="5" width="8.6" height="3.4" rx="1.2" />
        <motion.g variants={staggerG}>
          {[
            [8.6, 11.6], [12, 11.6], [15.4, 11.6],
            [8.6, 14.8], [12, 14.8], [15.4, 14.8],
            [8.6, 18], [12, 18], [15.4, 18],
          ].map(([cx, cy], i) => (
            <motion.circle key={i} cx={cx} cy={cy} r="0.7" fill="currentColor"
              variants={keyVar} style={{ ...VB, transformOrigin: `${cx}px ${cy}px` }} />
          ))}
        </motion.g>
      </>
    ),
  },
  'bet-archive-search': { // archive box
    origin: 'center',
    wrap: wrap({ rotate: [0, -6, 3, 0], scale: 1.12, transition: { rotate: WOBBLE, scale: BOING } }),
    svg: '<rect x="3.4" y="4.4" width="17.2" height="4.4" rx="1.6" /><path d="M4.8 8.8 L5 18.3 Q5 19.6 6.4 19.6 L17.6 19.6 Q19 19.6 19 18.3 L19.2 8.8" /><path d="M9.6 12 Q9.4 11.8 9.8 11.8 L14.2 11.8 Q14.6 11.8 14.4 12" /><path d="M11 4.4 L11 2.7 Q11 2 11.8 2.4 L13.4 3.6 Q13.8 3.9 13.4 4.2 L13 4.4" />',
  },
  'hacked-accounts': { // shield with a keyhole — shakes on hover, like someone trying the lock
    origin: 'center',
    wrap: wrap({ x: [0, -1.6, 1.6, -1, 0], scale: 1.1, transition: { x: WOBBLE, scale: SNAPPY } }),
    svg: '<path d="M12 3.2 Q15.6 5 19.3 5.4 Q19.8 5.5 19.8 6 L19.6 11.6 Q19.2 17.3 12.2 20.8 Q12 20.9 11.8 20.8 Q4.8 17.3 4.4 11.6 L4.2 6 Q4.2 5.5 4.7 5.4 Q8.4 5 12 3.2 Z" /><circle cx="12" cy="10.4" r="1.7" /><path d="M12 12.1 L12 15" />',
  },
  'limit-lookup': { // gauge — needle sweeps
    origin: 'center',
    wrap: wrap({ scale: 1.1, transition: { scale: SNAPPY } }),
    render: () => (
      <>
        <path d="M4.4 16.6 Q4 11.4 7.7 8.4 Q12 5 16.4 8.4 Q20 11.4 19.6 16.6" />
        <line x1="4.5" y1="16.6" x2="6.2" y2="16.6" />
        <line x1="17.8" y1="16.6" x2="19.5" y2="16.6" />
        <line x1="6.4" y1="11.1" x2="7.6" y2="11.9" />
        <line x1="12" y1="7.6" x2="12" y2="9" />
        <line x1="17.6" y1="11.1" x2="16.4" y2="11.9" />
        <motion.path d="M12 16 L15.4 11.4"
          variants={{ rest: { rotate: 0 }, hover: { rotate: [-38, 12, 0], transition: { duration: 0.5, ease: BACK } } }}
          style={{ ...VB, transformOrigin: '12px 16px' }} />
        <circle cx="12" cy="16" r="1.5" fill="currentColor" />
      </>
    ),
  },
  'affiliate-commission': { // swap arrows
    origin: 'center',
    wrap: wrap({ x: [0, 2, -2, 1, 0], scale: 1.1, transition: { x: WOBBLE, scale: SNAPPY } }),
    svg: '<path d="M5.4 8.6 L17.4 8.5" /><path d="M14.6 5.4 Q17.4 8 18.6 8.6 Q17.4 9.2 14.6 11.7" /><path d="M18.6 15.4 L6.6 15.5" /><path d="M9.4 12.3 Q6.6 15 5.4 15.4 Q6.6 16 9.4 18.6" />',
  },
  'birthday-bonus': { // cake — candle flickers, cake bounces
    origin: 'bottom',
    wrap: wrap({ scale: 1.1, y: [0, -2, 0], rotate: [0, -3, 2, 0], transition: { scale: SNAPPY, y: WOBBLE, rotate: WOBBLE } }),
    svg: '<path d="M12 6.1 Q13.3 7.5 12 9 Q10.7 7.5 12 6.1 Z" /><line x1="12" y1="9" x2="12" y2="14.2" /><path d="M4.4 15.2 Q6.4 13.5 8.4 15.2 Q10.4 16.9 12.4 15.2 Q14.4 13.5 16.4 15.2 Q18 16.5 19.6 15.2" /><path d="M4.4 15.4 L4.4 19.4 Q4.4 20 5 20 L19 20 Q19.6 20 19.6 19.4 L19.6 15.4" /><line x1="3.2" y1="20.3" x2="20.8" y2="20.3" />',
  },
  'ladder-checker': { // ladder — leans in on hover, like being climbed
    origin: 'bottom',
    wrap: wrap({ scale: 1.1, rotate: [0, -5, 3, 0], transition: { scale: BOING, rotate: WOBBLE } }),
    svg: '<path d="M8.2 3.2 L6.4 20.8" /><path d="M17.6 3.2 L15.8 20.8" /><path d="M7.7 7.6 L17.1 7.6" /><path d="M7.3 11.7 L16.7 11.7" /><path d="M6.9 15.8 L16.3 15.8" /><path d="M6.5 19.9 L15.9 19.9" />',
  },
  'rtp-check': { // U-turn arrow — swings back on hover, the way back in
    origin: 'center',
    wrap: wrap({ rotate: [0, -24, 6, 0], scale: 1.1, transition: { rotate: { duration: 0.55, ease: BACK }, scale: SNAPPY } }),
    svg: '<path d="M8.6 5.2 L4.4 9.3 L8.6 13.4" /><path d="M4.6 9.3 L14.2 9.3 Q19.6 9.3 19.6 14.6 Q19.6 19.8 14.2 19.8 L9.8 19.8" />',
  },
  'welcome-offer-checker': { // open gift — the card art's glowing box: lid floats, light bursts
    origin: 'bottom',
    wrap: wrap({ scale: 1.1, y: [0, -1.5, 0], transition: { scale: SNAPPY, y: WOBBLE } }),
    render: () => (
      <>
        {/* box — open, with the rim of the opening */}
        <path d="M5.6 13.7 L5.9 19.6 Q5.9 20.6 6.9 20.6 L17.1 20.6 Q18.1 20.6 18.1 19.6 L18.4 13.7" />
        <path d="M4.8 13.8 Q12 13.1 19.2 13.8" />
        <path d="M12 13.5 L12 20.6" />
        {/* lid — already ajar at rest, lifts and tilts further on hover */}
        <motion.path d="M4.6 11.6 L4.9 8.7 Q5 8.1 5.6 8.1 L18.6 7.9 Q19.2 7.9 19.3 8.5 L19.6 11.4 Z"
          variants={{ rest: { y: 0, rotate: 0 }, hover: { y: [0, -3, -2], rotate: [0, -7, -4], transition: { duration: 0.45, ease: BACK } } }}
          style={{ ...VB, transformOrigin: '12px 9.8px' }} />
        {/* light — three rays that pop out with the lid */}
        <motion.g variants={staggerG}>
          {[[12, 6, 12, 3.2], [8.6, 6.5, 7, 4.3], [15.4, 6.5, 17, 4.3]].map(([x1, y1, x2, y2], i) => (
            <motion.line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
              variants={{ rest: { opacity: 0.85, scale: 1 }, hover: { opacity: [0.3, 1, 0.85], scale: [0.6, 1.2, 1], transition: { duration: 0.4, ease: BACK } } }}
              style={{ ...VB, transformOrigin: `${x1}px ${y1}px` }} />
          ))}
        </motion.g>
      </>
    ),
  },
  optimove: { // paper plane — flies
    origin: 'center',
    wrap: wrap({ x: [0, 3, 1], y: [0, -3, -1], rotate: [0, -8, -4], transition: { duration: 0.45, ease: BACK } }),
    svg: '<path d="M21 3.4 Q21.4 3 21 3.8 L13.6 20.2 Q13.3 20.9 12.9 20.2 L10.6 14.2 Q10.5 13.9 10.2 13.7 L3.9 11 Q3.2 10.7 3.8 10.4 Z" /><path d="M10.6 14 Q15.2 8.8 20.6 3.6" /><path d="M3.4 19.9 L5 18.4" /><path d="M5.6 21.2 L7.1 19.8" /><path d="M2.2 16.8 L3.5 15.6" />',
  },
  'quick-links': { // lightning — flashes
    origin: 'center',
    wrap: wrap({ scale: [1, 1.22, 0.95, 1.05, 1], rotate: [0, -6, 6, 0], transition: { scale: { duration: 0.45, ease: BACK }, rotate: WOBBLE } }),
    svg: '<path d="M13.4 2.8 L7.4 12.6 Q7 13.2 7.7 13.2 L11.6 13.1 Q12.4 13.1 12 13.9 L10.6 21.6 L17 11.4 Q17.4 10.8 16.7 10.8 L12.8 10.9 Q12 10.9 12.4 10.1 L13.4 2.8 Z" /><path d="M18.6 6.6 L20.4 5.6" /><path d="M19 9.4 L21 9.2" /><path d="M17.6 4 L18.8 2.6" />',
  },
  'time-converter': { // clock — hands spin
    origin: 'center',
    wrap: wrap({ scale: 1.1, transition: { scale: SNAPPY } }),
    render: () => (
      <>
        <circle cx="12" cy="12" r="8.3" />
        <circle cx="12" cy="4.4" r="0.55" fill="currentColor" />
        <circle cx="19.6" cy="12" r="0.55" fill="currentColor" />
        <circle cx="12" cy="19.6" r="0.55" fill="currentColor" />
        <circle cx="4.4" cy="12" r="0.55" fill="currentColor" />
        <motion.path d="M12 12 Q11.9 9.6 12 7.4"
          variants={{ rest: { rotate: 0 }, hover: { rotate: [0, 40, 30], transition: { duration: 0.7, ease: 'easeInOut' } } }}
          style={{ ...VB, transformOrigin: '12px 12px' }} />
        <motion.path d="M12 12 Q13.8 13 15 13.6"
          variants={{ rest: { rotate: 0 }, hover: { rotate: [0, 360], transition: { duration: 0.7, ease: 'easeInOut' } } }}
          style={{ ...VB, transformOrigin: '12px 12px' }} />
      </>
    ),
  },
  'countries-restrictions': { // globe — playful wobble
    origin: 'center',
    wrap: wrap({ rotate: [0, -12, 9, 0], scale: 1.1, transition: { rotate: WOBBLE, scale: SNAPPY } }),
    svg: '<circle cx="12" cy="12" r="8.3" /><path d="M3.8 12 L20.2 12" /><path d="M12 3.7 Q16.4 7.4 16.4 12 Q16.4 16.6 12 20.3 Q7.6 16.6 7.6 12 Q7.6 7.4 12 3.7 Z" /><path d="M5.2 8.4 Q12 6.2 18.8 8.4" /><path d="M5.2 15.6 Q12 17.8 18.8 15.6" />',
  },
};

export const CLAUDE_TOOL_ICON_NAMES = Object.keys(ICONS);

/**
 * ClaudeToolIcon — presentational. Hover/active state propagates from the parent
 * motion.button via variants; this only supplies the geometry + the per-glyph
 * cartoonish signature. `active` paints the glyph clay (the one warm pop).
 */
export default function ClaudeToolIcon({ name, active = false, size = 16, className = '' }) {
  const ic = ICONS[name];
  if (!ic) return null;
  const svgProps = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true,
  };
  return (
    <motion.span
      variants={ic.wrap}
      className={`inline-flex items-center justify-center ${className}`}
      style={{ transformOrigin: ic.origin || 'center', color: active ? CLAY : undefined }}
    >
      {ic.svg
        ? <svg {...svgProps} dangerouslySetInnerHTML={{ __html: ic.svg }} />
        : <svg {...svgProps}>{ic.render()}</svg>}
    </motion.span>
  );
}
